import { toRaw } from "vue"
import { dataSet } from "./dot"

/**
 * Checks if the value should be iterated into form data
 * 
 * @param {*} value 
 * @param {Boolean} shouldIterateArrays
 * @returns {Boolean}
 */
export const shouldIterate = (value, shouldIterateArrays = false) => value instanceof Object
    && !(value instanceof Date)
    && !(value instanceof File)
    && (!Array.isArray(value) || shouldIterateArrays)
    && (Object.keys(value).length > 0)

/**
 * Gets the key we should be using for form data
 * 
 * @param {String|undefined} key 
 * @param {String} innerKey 
 * @returns {String}
 */
export const getKey = (key, innerKey, dotKeys = true) => key === undefined
    ? innerKey
    : (
        dotKeys
            ? `${key}.${innerKey}`
            : `${key}[${innerKey}]`
    )

/**
 * Preforms a deep merge of 2 objets
 * 
 * @param {Object} object1 
 * @param {Object} object2 
 * @returns {Object}
 */
export const deepMerge = (object1, object2) => {
    Object.keys(object2)
        .forEach((key) => {
            if (!object1.hasOwnProperty(key) || !shouldIterate(object2[key])) {
                object1[key] = object2[key]
            } else {
                deepMerge(object1[key], object2[key])
            }
        })

    return object1
}

/**
 * Runs a callback against every item in a JSON object
 * 
 * @param {Object}   json 
 * @param {Function} callback 
 * @param {Boolean}  iterateArrays If we should run iterations against arrays or just the callback against them
 * @param {Boolean}  dotKeys If keys should be formatted as parent.child or parent[child]
 */
export const iterateNested = (json, callback, iterateArrays = false, dotKeys = false) => {
    Object.keys(json)
        .forEach(
            key => iterateNestedInnerObject(key, json[key], callback, iterateArrays, dotKeys)
        )
}

/**
 * Iterates an object recursively
 * 
 * @param {String}   key 
 * @param {*}        value 
 * @param {Function} callback 
 * @param {Boolean}  iterateArrays If we should run iterations against arrays or just the callback against them
 * @param {Boolean}  dotKeys If keys should be formatted as parent.child or parent[child]
 */
const iterateNestedInnerObject = (key, value, callback, iterateArrays = false, dotKeys = false) => {
    if (shouldIterate(value, iterateArrays)) {
        Object.keys(value)
            .forEach(
                innerKey => iterateNestedInnerObject(
                    getKey(key, innerKey, dotKeys),
                    value[innerKey],
                    callback,
                    iterateArrays,
                    dotKeys
                )
            )
    } else {
        callback(
            key,
            value
        )
    }
}

/**
 * Deep clone a given object
 * 
 * @param {Object} json 
 * @returns {Object}
 */
export const deepClone = (json) => {
    let data = {}

    iterateNested(
        json,
        (key, value) => {
            dataSet(data, key, toRaw(value))
        },
        false,
        true
    )

    return data
}